import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from '@mantine/core';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  FormField,
  TextInput,
  Select,
  Alert,
  Paper,
  Text,
  Badge,
  MobileListRow,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListStaffQuery,
  useAddUpdateStaffMutation,
  useDeleteStaffMutation,
  useListRolesQuery,
  useListWarehousesQuery,
} from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';

type StaffRow = {
  id: string;
  userId: string;
  roleId?: string | null;
  isActive: boolean;
  user?: { id?: string; name?: string; phone?: string; email?: string } | null;
  role?: { id?: string; name?: string } | null;
};

export function StaffPage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    roleId: '',
    warehouseIds: [] as string[],
  });

  const { data, isLoading, isFetching, isError } = useListStaffQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const { data: rolesData } = useListRolesQuery();
  const { data: warehousesData } = useListWarehousesQuery({ page: 1, pageLimit: 50 });
  const [save, { isLoading: saving }] = useAddUpdateStaffMutation();
  const [remove] = useDeleteStaffMutation();

  const list = data?.listStaff;
  const roleOptions = (rolesData?.listRoles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));
  const warehouseOptions = (warehousesData?.listWarehouses.rows ?? []).map((w) => ({
    value: w.id,
    label: w.name,
  }));

  function openCreate() {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', roleId: '', warehouseIds: [] });
    setModalOpen(true);
  }

  function openEdit(row: StaffRow) {
    setEditing(row);
    setForm({
      name: row.user?.name || '',
      phone: row.user?.phone || '',
      email: row.user?.email || '',
      roleId: row.roleId || row.role?.id || '',
      warehouseIds: [],
    });
    setModalOpen(true);
  }

  async function onSave() {
    await save({
      ...(editing ? { id: editing.id } : {}),
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      roleId: form.roleId || undefined,
    }).unwrap();
    setModalOpen(false);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('nav.staff')}
        description={t('staff.description')}
      />
      {isError ? <Alert color="red" title={t('staff.loadFailed')} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('staff.searchPlaceholder')}
        emptyTitle={t('staff.emptyTitle')}
        emptyDescription={t('staff.emptyDescription')}
        onRowClick={openEdit}
        mobileView="list"
        addButton={canMutate() ? <Button onClick={openCreate}>{t('staff.add')}</Button> : undefined}
        columns={[
          {
            key: 'name',
            header: t('common.name'),
            render: (row) => row.user?.name || t('common.emptyValue'),
          },
          {
            key: 'phone',
            header: t('common.phone'),
            render: (row) => row.user?.phone || t('common.emptyValue'),
          },
          {
            key: 'role',
            header: t('staff.role'),
            render: (row) => row.role?.name || t('common.emptyValue'),
          },
          {
            key: 'status',
            header: t('common.status'),
            render: (row) => (
              <Badge color={row.isActive ? 'green' : 'gray'}>
                {row.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            ),
          },
        ]}
        renderCard={(row: StaffRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.user?.name || t('staff.unnamed')}</Text>
                <Badge color={row.isActive ? 'green' : 'gray'}>
                  {row.isActive ? t('common.active') : t('common.inactive')}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {row.user?.phone || t('common.noPhone')} · {row.role?.name || t('staff.noRole')}
              </Text>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: StaffRow) => (
          <MobileListRow
            title={row.user?.name || t('staff.unnamed')}
            subtitle={row.user?.phone || t('common.noPhone')}
            onClick={() => openEdit(row)}
          />
        )}
      />
      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('staff.edit') : t('staff.add')}
      >
        <Stack gap="md">
          <FormField label={t('common.name')} required>
            <TextInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            />
          </FormField>
          <FormField label={t('common.phone')} required={!editing}>
            <TextInput
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
              disabled={Boolean(editing)}
            />
          </FormField>
          <FormField label={t('common.email')}>
            <TextInput
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
            />
          </FormField>
          <FormField label={t('staff.role')}>
            <Select
              clearable
              placeholder={t('common.optional')}
              value={form.roleId || null}
              onChange={(value) => setForm({ ...form, roleId: value || '' })}
              data={roleOptions}
            />
          </FormField>
          <FormField label={t('roles.warehouseAssignment')}>
            <MultiSelect
              placeholder={t('roles.allWarehouses')}
              value={form.warehouseIds}
              onChange={(values) => setForm({ ...form, warehouseIds: values })}
              data={warehouseOptions}
              clearable
            />
          </FormField>
          <Group justify="space-between">
            {editing ? (
              <Button
                color="red"
                variant="light"
                onClick={async () => {
                  await remove(editing.id);
                  setModalOpen(false);
                }}
              >
                {t('staff.deactivate')}
              </Button>
            ) : (
              <span />
            )}
            <Button
              loading={saving}
              disabled={!form.name.trim() || (!editing && !form.phone.trim())}
              onClick={() => void onSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
